'use client';

import React, { useState } from 'react';
import { IRLExample, WardIRLData } from '../data/irlLensData';
import FissionDiagram from './FissionDiagram';

// 8-layer architecture aligned to the letter's framework
const EIGHT_LAYERS = [
  { num: 1, id: 'hawas', arabic: 'حَوَاس', label: 'Hawas', subtitle: 'Sensory Encounter', color: '#2d8a87', desc: 'Direct perception through the 5 senses — the entry point of every IRL encounter.' },
  { num: 2, id: 'zahir', arabic: 'ظَاهِر', label: 'Zahir', subtitle: 'The Apparent', color: '#2d7a47', desc: 'The outer, visible face of the Ayah. What is it? What does it look like?' },
  { num: 3, id: 'sunnat', arabic: 'سُنَّة', label: 'Sunnat Allah', subtitle: 'The Divine System', color: '#c9a84c', desc: 'The processes and patterns — Amr (commands) operating through creation.' },
  { num: 4, id: 'names', arabic: 'أَسْمَاء', label: 'Names Fission', subtitle: 'Divine Attributes', color: '#b87a2a', desc: 'The Names and Attributes of Allah manifest in the Sunnat processes.' },
  { num: 5, id: 'haqq', arabic: 'حَقّ', label: 'Haqq', subtitle: 'Truth / Reality / Right', color: '#8a4a2a', desc: 'The deeper truth — Zahir and Batin together. What rights does this reality carry?' },
  { num: 6, id: 'amanah', arabic: 'أَمَانَة', label: 'Amanah', subtitle: 'The Trust', color: '#6a3a7a', desc: 'The caretaking responsibility that recognition of Haqq calls us to.' },
  { num: 7, id: 'islah', arabic: 'إِصْلَاح', label: 'Islah', subtitle: 'Cultivate / Mend / Restore', color: '#4a6a3a', desc: 'The practical work — Islah and Salah share the same root. Action from recognition.' },
  { num: 8, id: 'uboor', arabic: 'عُبُور', label: 'Uboor', subtitle: 'The Bridge / Crossing', color: '#2d8a87', desc: 'Recognition becomes Caretaking. Bridge to resources, community, and ongoing work.' },
];

type Stage = 'zahir' | 'sunnat' | 'haqq' | 'amanah' | 'uboor';

interface DepthPanelProps {
  stage: Stage;
  example: IRLExample | null;
  wardData: WardIRLData | null;
  activeCity: string;
}

function NameTag({ arabic, transliteration, meaning }: { arabic: string; transliteration: string; meaning: string }) {
  return (
    <div className="inline-flex flex-col items-start border border-white/20 px-3 py-2 bg-white/3 mr-2 mb-2">
      <span className="font-serif text-lg text-white leading-none">{arabic}</span>
      <span className="font-mono text-[10px] text-white/70 tracking-wider mt-0.5">{transliteration}</span>
      <span className="font-mono text-[9px] text-white/40 tracking-wide">{meaning}</span>
    </div>
  );
}

function AyatBlock({ ref: ref_, arabic, translation }: { ref: string; arabic: string; translation: string }) {
  return (
    <div className="border-l-2 border-white/20 pl-4 py-2 mb-3">
      <div className="font-mono text-[10px] text-white/40 tracking-widest mb-1">{ref_}</div>
      <div className="font-serif text-base text-white/80 leading-relaxed text-right mb-1" dir="rtl">{arabic}</div>
      <div className="font-mono text-xs text-white/60 leading-relaxed italic">{translation}</div>
    </div>
  );
}

// ─── VISUAL SUNNAT STEP CARD ──────────────────────────────────────────────────
function SunnatStepCard({
  process,
  stepNum,
  total,
  isExpanded,
  onToggle,
}: {
  process: { processName: string; namesOfAllah: { arabic: string; transliteration: string; meaning: string }[]; description: string; equation?: string; stemDepth?: string; psychDepth?: string };
  stepNum: number;
  total: number;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  return (
    <div style={{ position: 'relative', marginBottom: 0 }}>
      {/* Flow line connecting steps */}
      {stepNum < total && (
        <div style={{
          position: 'absolute',
          left: 19,
          top: '100%',
          width: 2,
          height: 16,
          background: 'linear-gradient(to bottom, rgba(201,168,76,0.4), rgba(201,168,76,0.1))',
          zIndex: 1,
        }} />
      )}

      <div
        style={{
          border: isExpanded ? '1px solid rgba(201,168,76,0.4)' : '1px solid rgba(255,255,255,0.1)',
          background: isExpanded ? 'rgba(201,168,76,0.05)' : 'rgba(255,255,255,0.02)',
          borderRadius: 6,
          marginBottom: 16,
          transition: 'all 0.2s',
          cursor: 'pointer',
        }}
        onClick={onToggle}
      >
        {/* Step header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px' }}>
          {/* Numbered badge */}
          <div style={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            background: isExpanded ? 'rgba(201,168,76,0.9)' : 'rgba(201,168,76,0.15)',
            border: `2px solid ${isExpanded ? '#c9a84c' : 'rgba(201,168,76,0.3)'}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            transition: 'all 0.2s',
          }}>
            <span style={{
              fontFamily: 'monospace',
              fontSize: 11,
              fontWeight: 700,
              color: isExpanded ? '#060d12' : '#c9a84c',
            }}>{stepNum}</span>
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: 'monospace', fontSize: 11, color: isExpanded ? '#c9a84c' : 'rgba(255,255,255,0.7)', letterSpacing: '.06em', fontWeight: 600 }}>
              {process.processName}
            </div>
            {process.equation && (
              <div style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(255,255,255,0.3)', marginTop: 2 }}>
                {process.equation}
              </div>
            )}
          </div>

          {/* Active Names pills */}
          <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
            {process.namesOfAllah.slice(0, 2).map((n, i) => (
              <div key={i} style={{
                background: 'rgba(201,168,76,0.1)',
                border: '1px solid rgba(201,168,76,0.2)',
                borderRadius: 3,
                padding: '2px 6px',
                fontFamily: "'Amiri', serif",
                fontSize: 12,
                color: '#c9a84c',
              }}>
                {n.arabic}
              </div>
            ))}
          </div>

          <span style={{ fontFamily: 'monospace', fontSize: 12, color: 'rgba(255,255,255,0.3)', marginLeft: 4 }}>
            {isExpanded ? '−' : '+'}
          </span>
        </div>

        {/* Expanded content */}
        {isExpanded && (
          <div style={{ padding: '0 16px 16px', borderTop: '1px solid rgba(201,168,76,0.1)' }}>
            <p style={{ fontFamily: 'monospace', fontSize: 11, color: 'rgba(255,255,255,0.6)', lineHeight: 1.7, marginTop: 12, marginBottom: 12 }}>
              {process.description}
            </p>

            {/* Names active at this step */}
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(255,255,255,0.3)', letterSpacing: '.12em', textTransform: 'uppercase', marginBottom: 8 }}>
                NAMES ACTIVE AT THIS STEP
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {process.namesOfAllah.map((n, i) => (
                  <div key={i} style={{
                    background: 'rgba(201,168,76,0.08)',
                    border: '1px solid rgba(201,168,76,0.2)',
                    borderRadius: 4,
                    padding: '6px 10px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}>
                    <span style={{ fontFamily: "'Amiri', serif", fontSize: 18, color: '#c9a84c', lineHeight: 1 }}>{n.arabic}</span>
                    <span style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(255,255,255,0.5)', marginTop: 3 }}>{n.transliteration}</span>
                    <span style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(255,255,255,0.3)', marginTop: 1 }}>{n.meaning}</span>
                  </div>
                ))}
              </div>
            </div>

            {process.stemDepth && (
              <div style={{ background: 'rgba(45,138,135,0.06)', border: '1px solid rgba(45,138,135,0.15)', borderRadius: 4, padding: '8px 12px', marginBottom: 8 }}>
                <div style={{ fontFamily: 'monospace', fontSize: 9, color: '#2d8a87', letterSpacing: '.1em', marginBottom: 4 }}>STEM DEPTH</div>
                <p style={{ fontFamily: 'monospace', fontSize: 10, color: 'rgba(255,255,255,0.4)', lineHeight: 1.6 }}>{process.stemDepth}</p>
              </div>
            )}

            {process.psychDepth && (
              <div style={{ background: 'rgba(106,58,122,0.06)', border: '1px solid rgba(106,58,122,0.15)', borderRadius: 4, padding: '8px 12px' }}>
                <div style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(180,120,220,0.7)', letterSpacing: '.1em', marginBottom: 4 }}>PSYCHOLOGICAL LAYER</div>
                <p style={{ fontFamily: 'monospace', fontSize: 10, color: 'rgba(255,255,255,0.4)', lineHeight: 1.6 }}>{process.psychDepth}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── 8-LAYER ARCHITECTURE PANEL ───────────────────────────────────────────────
function EightLayerPanel({ activeStage }: { activeStage: Stage }) {
  const stageToLayer: Record<Stage, number> = {
    zahir: 2,
    sunnat: 3,
    haqq: 5,
    amanah: 6,
    uboor: 8,
  };
  const activeLayer = stageToLayer[activeStage];

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(255,255,255,0.3)', letterSpacing: '.15em', textTransform: 'uppercase', marginBottom: 12 }}>
        8-LAYER RECOGNITION PATHWAY
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {EIGHT_LAYERS.map((layer) => {
          const isActive = layer.num === activeLayer;
          const isPast = layer.num < activeLayer;
          return (
            <div key={layer.id} style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '6px 10px',
              borderRadius: 4,
              background: isActive ? `${layer.color}18` : 'transparent',
              border: isActive ? `1px solid ${layer.color}44` : '1px solid transparent',
              opacity: isPast ? 0.5 : isActive ? 1 : 0.35,
              transition: 'all 0.3s',
            }}>
              <div style={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                background: isActive ? layer.color : isPast ? `${layer.color}44` : 'rgba(255,255,255,0.05)',
                border: `1px solid ${isActive ? layer.color : 'rgba(255,255,255,0.1)'}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}>
                <span style={{ fontFamily: 'monospace', fontSize: 8, color: isActive ? '#060d12' : 'rgba(255,255,255,0.4)', fontWeight: 700 }}>
                  {layer.num}
                </span>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                  <span style={{ fontFamily: 'monospace', fontSize: 10, color: isActive ? layer.color : 'rgba(255,255,255,0.4)', fontWeight: isActive ? 700 : 400 }}>
                    {layer.label}
                  </span>
                  <span style={{ fontFamily: "'Amiri', serif", fontSize: 12, color: isActive ? layer.color : 'rgba(255,255,255,0.2)' }}>
                    {layer.arabic}
                  </span>
                </div>
                {isActive && (
                  <div style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>
                    {layer.subtitle}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function DepthPanel({ stage, example, wardData, activeCity }: DepthPanelProps) {
  const [expandedProcess, setExpandedProcess] = useState<number | null>(null);
  const [showFission, setShowFission] = useState(false);
  const [showEpistemology, setShowEpistemology] = useState(false);
  const [showPsychology, setShowPsychology] = useState(false);
  const [showLayerMap, setShowLayerMap] = useState(false);

  if (!example) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="font-serif text-3xl text-white/20 mb-3">آيَة</div>
          <div className="font-mono text-xs text-white/30 tracking-widest">SELECT AN ENCOUNTER</div>
          <div className="font-mono text-[10px] text-white/20 mt-2">Choose a ward and Ayah to begin</div>
        </div>
      </div>
    );
  }

  // ── ZAHIR ──────────────────────────────────────────────────────────────────
  if (stage === 'zahir') {
    return (
      <div className="space-y-6">
        {/* 8-layer toggle */}
        <div className="border border-white/10">
          <button
            onClick={() => setShowLayerMap(!showLayerMap)}
            className="w-full flex items-center justify-between px-4 py-3 font-mono text-[10px] tracking-widest text-white/50 hover:text-white/80 transition-colors"
          >
            <span>8-LAYER RECOGNITION PATHWAY</span>
            <span className="text-white/30">{showLayerMap ? '−' : '+'}</span>
          </button>
          {showLayerMap && (
            <div className="px-4 pb-4">
              <EightLayerPanel activeStage={stage} />
            </div>
          )}
        </div>

        <div>
          <div className="font-mono text-[10px] tracking-widest text-white/40 mb-1">LAYER 01–02 — HAWAS · حَوَاس → ZAHIR · ظَاهِر</div>
          <h2 className="font-mono text-xl text-white tracking-wide">{example.name}</h2>
          {example.arabicName && (
            <div className="font-serif text-2xl text-white/60 mt-1">{example.arabicName}</div>
          )}
        </div>

        <div className="border border-white/10 p-4 bg-white/2">
          <div className="font-mono text-[10px] tracking-widest text-white/40 mb-3">SENSORY ENCOUNTER — HAWAS (Layer 1)</div>
          <p className="font-mono text-sm text-white/70 leading-relaxed">{example.zahir.sensory}</p>
          <div className="grid grid-cols-2 gap-3 mt-4">
            {example.zahir.sight && (
              <div>
                <div className="font-mono text-[9px] text-white/30 tracking-widest mb-1">SIGHT · بَصَر</div>
                <p className="font-mono text-xs text-white/60">{example.zahir.sight}</p>
              </div>
            )}
            {example.zahir.smell && (
              <div>
                <div className="font-mono text-[9px] text-white/30 tracking-widest mb-1">SMELL · شَمّ</div>
                <p className="font-mono text-xs text-white/60">{example.zahir.smell}</p>
              </div>
            )}
            {example.zahir.texture && (
              <div>
                <div className="font-mono text-[9px] text-white/30 tracking-widest mb-1">TEXTURE · لَمْس</div>
                <p className="font-mono text-xs text-white/60">{example.zahir.texture}</p>
              </div>
            )}
            {example.zahir.sound && (
              <div>
                <div className="font-mono text-[9px] text-white/30 tracking-widest mb-1">SOUND · سَمْع</div>
                <p className="font-mono text-xs text-white/60">{example.zahir.sound}</p>
              </div>
            )}
          </div>
        </div>

        {/* Three Questions — Tier 1 */}
        <div>
          <div className="font-mono text-[10px] tracking-widest text-white/40 mb-3">THE THREE QUESTIONS — الأَسْئِلَة الثَّلَاثَة (Layer 2)</div>
          <div className="grid grid-cols-1 gap-3">
            <div className="border border-white/15 p-4">
              <div className="font-mono text-xs text-white tracking-widest mb-1">IS IT? · هَلْ هُوَ؟</div>
              <div className="font-mono text-[10px] text-white/50 mb-2">WUJUD — Existence confirmed</div>
              <p className="font-mono text-xs text-white/60 leading-relaxed">Yes — it exists. Its existence is Dalil (evidence) of the Necessary Existent. Al-Wajid (The Finder/Perceiver), Al-Haqq (The Real).</p>
            </div>
            <div className="border border-white/15 p-4">
              <div className="font-mono text-xs text-white tracking-widest mb-1">WHAT IS IT? · مَا هُوَ؟</div>
              <div className="font-mono text-[10px] text-white/50 mb-2">MAHIYYA — Essence/Quiddity</div>
              <p className="font-mono text-xs text-white/60 leading-relaxed">{example.zahir.huduthDhati}</p>
            </div>
            <div className="border border-white/15 p-4">
              <div className="font-mono text-xs text-white tracking-widest mb-1">WHEN IS/WAS IT? · مَتَى؟</div>
              <div className="font-mono text-[10px] text-white/50 mb-2">HUDUTH ZAMANI — Temporal origination</div>
              <p className="font-mono text-xs text-white/60 leading-relaxed">{example.zahir.huduthZamani}</p>
            </div>
          </div>
        </div>

        {/* Tier 2 — Optional depth */}
        <div className="border border-white/10">
          <button
            onClick={() => setShowFission(!showFission)}
            className="w-full flex items-center justify-between px-4 py-3 font-mono text-[10px] tracking-widest text-white/50 hover:text-white/80 transition-colors"
          >
            <span>DEPTH LAYER — FISSION CHAIN DIAGRAM</span>
            <span className="text-white/30">{showFission ? '−' : '+'}</span>
          </button>
          {showFission && (
            <div className="px-4 pb-4">
              <p className="font-mono text-xs text-white/50 leading-relaxed mb-4">
                The Fikr (reflection) process made visual — how a single observation cascades into recognition of Allah's attributes through the chain of logical necessity.
              </p>
              <FissionDiagram />
            </div>
          )}
        </div>

        <div className="border border-white/10">
          <button
            onClick={() => setShowEpistemology(!showEpistemology)}
            className="w-full flex items-center justify-between px-4 py-3 font-mono text-[10px] tracking-widest text-white/50 hover:text-white/80 transition-colors"
          >
            <span>DEPTH LAYER — ISLAMIC EPISTEMOLOGY</span>
            <span className="text-white/30">{showEpistemology ? '−' : '+'}</span>
          </button>
          {showEpistemology && (
            <div className="px-4 pb-4 space-y-3">
              <div className="border border-white/10 p-3">
                <div className="font-mono text-[10px] text-white/60 tracking-widest mb-1">HAWAS · حَوَاس — Sensory Perception</div>
                <p className="font-mono text-xs text-white/50 leading-relaxed">The senses are the first gateway of knowledge — gifts of Al-Basir (The All-Seeing) and As-Sami (The All-Hearing). What we perceive through the senses is the Zahir — the apparent reality.</p>
              </div>
              <div className="border border-white/10 p-3">
                <div className="font-mono text-[10px] text-white/60 tracking-widest mb-1">KHABAR · خَبَر — Transmitted Knowledge</div>
                <p className="font-mono text-xs text-white/50 leading-relaxed">Knowledge transmitted through reliable chains — Quran, Sunnah, scholarly tradition. Al-Alim (The All-Knowing) is the source of all transmitted knowledge.</p>
              </div>
              <div className="border border-white/10 p-3">
                <div className="font-mono text-[10px] text-white/60 tracking-widest mb-1">AQL · عَقْل — Rational Deduction</div>
                <p className="font-mono text-xs text-white/50 leading-relaxed">The intellect that draws conclusions from observation and transmitted knowledge. The Three Questions are an exercise of Aql — rational deduction leading to recognition of the Necessary Existent.</p>
              </div>
              <div className="border border-white/10 p-3">
                <div className="font-mono text-[10px] text-white/60 tracking-widest mb-1">ADAM TAUGHT THE NAMES · 2:31</div>
                <div className="font-serif text-sm text-white/70 text-right mb-1" dir="rtl">وَعَلَّمَ آدَمَ الْأَسْمَاءَ كُلَّهَا</div>
                <p className="font-mono text-xs text-white/50 leading-relaxed">"And He taught Adam the names of all things." The act of naming is the foundation of all human knowledge and caretaking — Al-Alim's knowledge distributed to humanity as Amanah.</p>
              </div>
            </div>
          )}
        </div>

        <div className="border border-white/10">
          <button
            onClick={() => setShowPsychology(!showPsychology)}
            className="w-full flex items-center justify-between px-4 py-3 font-mono text-[10px] tracking-widest text-white/50 hover:text-white/80 transition-colors"
          >
            <span>DEPTH LAYER — PSYCHOLOGICAL PERCEPTION</span>
            <span className="text-white/30">{showPsychology ? '−' : '+'}</span>
          </button>
          {showPsychology && (
            <div className="px-4 pb-4 space-y-3">
              <p className="font-mono text-xs text-white/50 leading-relaxed">
                How human perception processes the encountered thing — and how each stage maps to Names of Allah:
              </p>
              <div className="space-y-2">
                {[
                  { stage: 'Sensory Input', name: 'Al-Basir / As-Sami', arabic: 'الْبَصِير / السَّمِيع', desc: 'Raw sensory data arrives — sight, sound, smell, touch. We are given sight and hearing as a trust.' },
                  { stage: 'Pattern Recognition', name: 'Al-Khabir', arabic: 'الْخَبِير', desc: 'The mind recognizes patterns — this is a tree, this is water. Al-Khabir (The All-Aware) gave us the capacity for deeper-than-surface perception.' },
                  { stage: 'Naming', name: 'Al-Alim', arabic: 'الْعَلِيم', desc: 'We name the thing — "fruit tree," "river," "bird." Naming is the foundation of all knowledge, given to Adam (2:31).' },
                  { stage: 'Meaning-Making', name: 'Al-Hakim', arabic: 'الْحَكِيم', desc: 'We assign meaning — "this is food," "this is beautiful," "this is a sign." Al-Hakim\'s wisdom is the capacity to extract meaning from observation.' },
                  { stage: 'Fikr (Reflection)', name: 'Al-Latif', arabic: 'اللَّطِيف', desc: 'Deep reflection on what has been perceived — the subtle connections between the Zahir and the Batin. Al-Latif operates at the level of subtle awareness.' },
                ].map((item, i) => (
                  <div key={i} className="border border-white/10 p-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-mono text-[10px] text-white/60 tracking-wider">{item.stage}</span>
                      <span className="font-serif text-sm text-white/50">{item.arabic}</span>
                    </div>
                    <div className="font-mono text-[9px] text-white/40 mb-1">{item.name}</div>
                    <p className="font-mono text-[10px] text-white/40 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── SUNNAT ALLAH ───────────────────────────────────────────────────────────
  if (stage === 'sunnat') {
    return (
      <div className="space-y-6">
        {/* 8-layer indicator */}
        <div className="border border-white/10">
          <button
            onClick={() => setShowLayerMap(!showLayerMap)}
            className="w-full flex items-center justify-between px-4 py-3 font-mono text-[10px] tracking-widest text-white/50 hover:text-white/80 transition-colors"
          >
            <span>8-LAYER PATHWAY — LAYER 3: SUNNAT ALLAH</span>
            <span className="text-white/30">{showLayerMap ? '−' : '+'}</span>
          </button>
          {showLayerMap && (
            <div className="px-4 pb-4">
              <EightLayerPanel activeStage={stage} />
            </div>
          )}
        </div>

        <div>
          <div className="font-mono text-[10px] tracking-widest text-white/40 mb-1">LAYER 03 — SUNNAT ALLAH · سُنَّةُ اللَّه</div>
          <h2 className="font-mono text-xl text-white tracking-wide">The Patterns & Processes</h2>
          <p className="font-mono text-xs text-white/50 mt-2 leading-relaxed">
            كُنْ فَيَكُونُ — Be, and it is. Each process is a divine command operating through creation.
          </p>
        </div>

        {/* Visual Sunnat Step Cards */}
        <div>
          <div className="font-mono text-[9px] tracking-widest text-white/30 mb-3">
            SUNNAT STEPS — {example.sunnatProcesses.length} PROCESSES IDENTIFIED
          </div>
          {example.sunnatProcesses.map((process, i) => (
            <SunnatStepCard
              key={i}
              process={process}
              stepNum={i + 1}
              total={example.sunnatProcesses.length}
              isExpanded={expandedProcess === i}
              onToggle={() => setExpandedProcess(expandedProcess === i ? null : i)}
            />
          ))}
        </div>

        {/* Layer 4: Names Fission teaser */}
        <div style={{
          background: 'rgba(201,168,76,0.04)',
          border: '1px solid rgba(201,168,76,0.15)',
          borderRadius: 6,
          padding: '12px 16px',
        }}>
          <div className="font-mono text-[9px] tracking-widest text-white/40 mb-2">LAYER 04 — NAMES FISSION</div>
          <p className="font-mono text-xs text-white/50 leading-relaxed">
            Each Sunnat process above illuminates specific Names and Attributes of Allah. Proceed to Haqq to see how these Names compose the deeper reality of this Ayah.
          </p>
        </div>
      </div>
    );
  }

  // ── HAQQ ───────────────────────────────────────────────────────────────────
  if (stage === 'haqq') {
    return (
      <div className="space-y-6">
        {/* 8-layer indicator */}
        <div className="border border-white/10">
          <button
            onClick={() => setShowLayerMap(!showLayerMap)}
            className="w-full flex items-center justify-between px-4 py-3 font-mono text-[10px] tracking-widest text-white/50 hover:text-white/80 transition-colors"
          >
            <span>8-LAYER PATHWAY — LAYERS 4–5: NAMES → HAQQ</span>
            <span className="text-white/30">{showLayerMap ? '−' : '+'}</span>
          </button>
          {showLayerMap && (
            <div className="px-4 pb-4">
              <EightLayerPanel activeStage={stage} />
            </div>
          )}
        </div>

        <div>
          <div className="font-mono text-[10px] tracking-widest text-white/40 mb-1">LAYER 05 — HAQQ · حَقّ</div>
          <h2 className="font-mono text-xl text-white tracking-wide">The Truth / Reality / Right</h2>
        </div>

        <div className="border border-white/15 p-4 bg-white/2">
          <div className="font-mono text-[9px] tracking-widest text-white/30 mb-2">ZAHIR (Layer 2)</div>
          <p className="font-mono text-sm text-white/70 leading-relaxed">{example.haqq.zahirStatement}</p>
        </div>

        <div className="border border-white/15 p-4 bg-white/2">
          <div className="font-mono text-[9px] tracking-widest text-white/30 mb-2">BATIN · بَاطِن (Layer 6)</div>
          <p className="font-mono text-sm text-white/70 leading-relaxed">{example.haqq.batinStatement}</p>
        </div>

        <div>
          <div className="font-mono text-[10px] tracking-widest text-white/40 mb-3">QURAN AYAT</div>
          {example.haqq.ayat.map((ayat, i) => (
            <AyatBlock key={i} {...ayat} />
          ))}
        </div>

        {/* Layer 4: All Names from Sunnat processes */}
        <div>
          <div className="font-mono text-[9px] tracking-widest text-white/30 mb-3">LAYER 04 — ALL NAMES ACTIVE IN THIS AYAH</div>
          <div className="flex flex-wrap">
            {example.sunnatProcesses.flatMap(p => p.namesOfAllah).filter((n, i, arr) =>
              arr.findIndex(x => x.transliteration === n.transliteration) === i
            ).map((name, i) => (
              <NameTag key={i} {...name} />
            ))}
          </div>
        </div>

        <div className="border border-white/10 p-4">
          <div className="font-mono text-[9px] tracking-widest text-white/30 mb-2">THE AMANAH BRIDGE (Layer 6)</div>
          <p className="font-mono text-xs text-white/60 leading-relaxed">
            The Haqq of this Ayah presents an Amanah. What does recognition of Allah's Names in this creation call the Caretaker to?
          </p>
          <button
            onClick={() => {}}
            className="mt-3 font-mono text-[10px] tracking-widest text-white/50 border border-white/20 px-4 py-2 hover:border-white/50 hover:text-white/80 transition-colors"
          >
            PROCEED TO AMANAH →
          </button>
        </div>
      </div>
    );
  }

  // ── AMANAH ─────────────────────────────────────────────────────────────────
  if (stage === 'amanah') {
    return (
      <div className="space-y-6">
        {/* 8-layer indicator */}
        <div className="border border-white/10">
          <button
            onClick={() => setShowLayerMap(!showLayerMap)}
            className="w-full flex items-center justify-between px-4 py-3 font-mono text-[10px] tracking-widest text-white/50 hover:text-white/80 transition-colors"
          >
            <span>8-LAYER PATHWAY — LAYERS 6–7: AMANAH → ISLAH</span>
            <span className="text-white/30">{showLayerMap ? '−' : '+'}</span>
          </button>
          {showLayerMap && (
            <div className="px-4 pb-4">
              <EightLayerPanel activeStage={stage} />
            </div>
          )}
        </div>

        <div>
          <div className="font-mono text-[10px] tracking-widest text-white/40 mb-1">LAYER 06 — AMANAH · أَمَانَة</div>
          <h2 className="font-mono text-xl text-white tracking-wide">The Trust</h2>
        </div>

        <div className="border border-white/15 p-4 bg-white/2">
          <p className="font-mono text-sm text-white/70 leading-relaxed">{example.amanah.statement}</p>
        </div>

        {wardData && (
          <div className="border border-white/15 p-4">
            <div className="font-mono text-[9px] tracking-widest text-white/30 mb-2">IN {wardData.wardName.toUpperCase()}</div>
            <p className="font-mono text-xs text-white/60 leading-relaxed">
              This ward has been identified as a priority area for caretaking. The Rizq exists — the Amanah is to trace where the barriers are and bridge to Islah.
            </p>
          </div>
        )}

        <div>
          <div className="font-mono text-[10px] tracking-widest text-white/40 mb-3">NAMES ACTIVE AT THIS STAGE</div>
          <div className="flex flex-wrap">
            {example.amanah.namesActive.map((name, i) => (
              <NameTag key={i} {...name} />
            ))}
          </div>
        </div>

        {/* Layer 7: Islah */}
        <div style={{
          background: 'rgba(74,106,58,0.06)',
          border: '1px solid rgba(74,106,58,0.2)',
          borderRadius: 6,
          padding: '12px 16px',
        }}>
          <div className="font-mono text-[9px] tracking-widest mb-2" style={{ color: 'rgba(120,180,100,0.7)' }}>LAYER 07 — ISLAH · إِصْلَاح</div>
          <p className="font-mono text-xs leading-relaxed" style={{ color: 'rgba(255,255,255,0.5)' }}>
            Islah and Salah share the same Arabic root. The practical work of caretaking — cultivate, mend, restore — flows from recognition of the Amanah.
          </p>
        </div>

        <div className="border-l-4 border-white/20 pl-4 py-2">
          <div className="font-mono text-[9px] tracking-widest text-white/30 mb-2">RESHAT AL-TAQWAH</div>
          <p className="font-mono text-xs text-white/50 leading-relaxed italic">{example.amanah.reshatReference}</p>
        </div>

        <div className="border border-white/10 p-4">
          <div className="font-serif text-sm text-white/60 text-right mb-2" dir="rtl">وَلِبَاسُ التَّقْوَىٰ ذَٰلِكَ خَيْرٌ</div>
          <p className="font-mono text-[10px] text-white/40 leading-relaxed">
            "And the garment of Taqwah — that is best." (7:26) — Recognition of Allah's Names in creation is a thread in this garment.
          </p>
        </div>
      </div>
    );
  }

  // ── UBOOR ──────────────────────────────────────────────────────────────────
  if (stage === 'uboor') {
    return (
      <div className="space-y-6">
        {/* 8-layer indicator */}
        <div className="border border-white/10">
          <button
            onClick={() => setShowLayerMap(!showLayerMap)}
            className="w-full flex items-center justify-between px-4 py-3 font-mono text-[10px] tracking-widest text-white/50 hover:text-white/80 transition-colors"
          >
            <span>8-LAYER PATHWAY — LAYER 8: UBOOR</span>
            <span className="text-white/30">{showLayerMap ? '−' : '+'}</span>
          </button>
          {showLayerMap && (
            <div className="px-4 pb-4">
              <EightLayerPanel activeStage={stage} />
            </div>
          )}
        </div>

        <div>
          <div className="font-mono text-[10px] tracking-widest text-white/40 mb-1">LAYER 08 — UBOOR · عُبُور</div>
          <h2 className="font-mono text-xl text-white tracking-wide">The Bridge / Crossing</h2>
          <p className="font-mono text-xs text-white/50 mt-2 leading-relaxed">
            Recognition becomes Caretaking. The Ayah bridges to resources in {wardData?.wardName ?? 'your ward'}.
          </p>
        </div>

        {/* Full 8-layer summary */}
        <div style={{
          background: 'rgba(45,138,135,0.04)',
          border: '1px solid rgba(45,138,135,0.15)',
          borderRadius: 6,
          padding: '12px 16px',
        }}>
          <div className="font-mono text-[9px] tracking-widest mb-3" style={{ color: 'rgba(45,138,135,0.8)' }}>
            RECOGNITION PATHWAY COMPLETE
          </div>
          <EightLayerPanel activeStage={stage} />
        </div>

        <div className="border border-white/15 p-4 bg-white/2">
          <div className="font-mono text-[9px] tracking-widest text-white/30 mb-2">LIVE RESOURCE BRIDGE</div>
          <p className="font-mono text-xs text-white/60 leading-relaxed mb-3">
            This Ayah bridges to {example.resourceCategories.length} resource categories in {wardData?.wardName ?? 'this ward'}.
          </p>
          <div className="flex flex-wrap gap-2">
            {example.resourceCategories.map((cat, i) => (
              <div key={i} className="border border-white/20 px-3 py-1.5 font-mono text-[10px] text-white/70 tracking-wider">
                {cat}
              </div>
            ))}
          </div>
        </div>

        {wardData && (
          <div className="border border-white/10 p-4">
            <div className="font-mono text-[9px] tracking-widest text-white/30 mb-3">CARETAKERS IN {wardData.wardName.toUpperCase()}</div>
            <div className="flex items-center gap-3">
              <div className="font-mono text-3xl text-white">{wardData.catalogCount}</div>
              <div className="font-mono text-[10px] text-white/40 leading-relaxed">
                Ayat cataloged by caretakers<br />in {wardData.wardName}
              </div>
            </div>
          </div>
        )}

        <div className="border border-white/15 p-4">
          <div className="font-mono text-[9px] tracking-widest text-white/30 mb-2">COLLECTIVE CATALOG</div>
          <p className="font-mono text-xs text-white/60 leading-relaxed">
            Caretakers across all wards have cataloged this type of Ayah. Each catalog entry strengthens the network of recognition — a collective Fikr operating across the Ummah.
          </p>
        </div>

        <a
          href="/muslim-resource-guide"
          className="block border border-white/30 p-4 hover:bg-white/5 transition-colors group"
        >
          <div className="font-mono text-[9px] tracking-widest text-white/40 mb-1">BRIDGE TO</div>
          <div className="font-mono text-sm text-white tracking-wider group-hover:text-white">Muslim Resource Guide →</div>
          <div className="font-mono text-[10px] text-white/40 mt-1">Find resources in {wardData?.wardName ?? 'your ward'} fisabilillah</div>
        </a>
      </div>
    );
  }

  return null;
}
